"use client"

import React, { useState } from "react"
import {
  Monitor,
  Smartphone,
  ChevronRight,
  BarChart3,
  Shield,
  Clock,
  Download,
  Camera,
  Eye,
  FileText,
  CreditCard,
  Bell,
  Monitor as MonitorIcon
} from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { PatternFish } from "@/components/ui/pattern-fish"
import { whatsappUrl } from "@/lib/contact"

interface ClientSystemProps {
  dict: {
    label: string
    title: string
    description: string
    features: {
      diary: { title: string; headline: string; description: string; bullets: string[] }
      financial: { title: string; headline: string; description: string; bullets: string[] }
      project: { title: string; headline: string; description: string; bullets: string[] }
      docs: { title: string; headline: string; description: string; bullets: string[] }
    }
    highlights: {
      security: { title: string; text: string }
      app: { title: string; text: string }
      access: { title: string; text: string }
      export: { title: string; text: string }
    }
    cta: string
  }
}

export function ClientSystem({ dict }: ClientSystemProps) {
  const features = [
    {
      id: "diary",
      icon: Camera,
      ...dict.features.diary,
    },
    {
      id: "financial",
      icon: CreditCard,
      ...dict.features.financial,
    },
    {
      id: "project",
      icon: MonitorIcon,
      ...dict.features.project,
    },
    {
      id: "docs",
      icon: FileText,
      ...dict.features.docs,
    },
  ]

  const highlights = [
    {
      icon: Shield,
      ...dict.highlights.security,
    },
    {
      icon: Smartphone,
      ...dict.highlights.app,
    },
    {
      icon: Clock,
      ...dict.highlights.access,
    },
    {
      icon: Download,
      ...dict.highlights.export,
    },
  ]

  const [activeFeature, setActiveFeature] = useState(features[0].id)
  const current = features.find((f) => f.id === activeFeature) ?? features[0]

  return (
    <section id="sistema" className="relative overflow-hidden bg-slate-50 py-16 lg:py-24">
      <PatternFish className="absolute inset-0 z-0 opacity-[0.03]" />
      
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
        {/* Section header */}
        <div className="max-w-3xl">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-accent">
            {dict.label}
          </p>
          <h2 className="mt-4 text-4xl font-black tracking-tight text-primary md:text-5xl lg:text-6xl text-balance">
            {dict.title}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground font-medium">
            {dict.description}
          </p>
        </div>

        {/* Feature nav + detail panel */}
        <div className="mt-20 flex flex-col gap-12 lg:flex-row">
          {/* Sidebar nav */}
          <nav className="flex flex-row gap-3 overflow-x-auto pb-4 lg:w-72 lg:flex-shrink-0 lg:flex-col lg:gap-2 lg:overflow-x-visible lg:pb-0">
            {features.map((feature) => {
              const isActive = feature.id === activeFeature
              return (
                <button
                  key={feature.id}
                  onClick={() => setActiveFeature(feature.id)}
                  className={`group flex flex-shrink-0 items-center gap-4 rounded-xl px-5 py-4 text-left transition-all duration-300 ${
                    isActive
                      ? "bg-primary text-white shadow-xl shadow-primary/20 scale-[1.02]"
                      : "bg-white border border-slate-200 text-slate-500 hover:border-accent/40 hover:text-primary"
                  }`}
                >
                  <feature.icon
                    className={`h-5 w-5 flex-shrink-0 transition-colors ${
                      isActive ? "text-accent" : "text-primary/40 group-hover:text-accent"
                    }`}
                  />
                  <span className="whitespace-nowrap text-xs font-black uppercase tracking-widest lg:whitespace-normal">
                    {feature.title}
                  </span>
                  <ChevronRight
                    className={`ml-auto hidden h-4 w-4 transition-transform group-hover:translate-x-1 lg:block ${
                      isActive ? "text-white" : "text-slate-300"
                    }`}
                  />
                </button>
              )
            })}
          </nav>

          {/* Detail panel */}
          <div className="flex-1 animate-in fade-in slide-in-from-right-4 duration-700">
            <Card className="overflow-hidden border-none bg-white shadow-2xl shadow-slate-200/60 rounded-3xl">
              <CardContent className="flex flex-col gap-8 p-6 lg:p-10">
                {/* Feature header */}
                <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-5">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 text-primary shadow-inner">
                      <current.icon className="h-8 w-8" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">
                        {current.title}
                      </span>
                      <h3 className="font-serif text-2xl font-black tracking-tight text-primary md:text-3xl">
                        {current.headline}
                      </h3>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2">
                       <Monitor className="h-3.5 w-3.5 text-slate-500" />
                       <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Web</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2">
                       <Smartphone className="h-3.5 w-3.5 text-slate-500" />
                       <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">App</span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-lg leading-relaxed text-slate-600 font-medium line-clamp-3">
                  {current.description}
                </p>

                {/* Bullet points */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {current.bullets.map((bullet) => (
                    <div
                      key={bullet}
                      className="group flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50/50 p-4 transition-all hover:border-accent/20 hover:bg-white hover:shadow-lg"
                    >
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-sm transition-colors group-hover:bg-accent group-hover:text-white">
                        <BarChart3 className="h-4 w-4" />
                      </span>
                      <span className="text-sm font-bold text-slate-700">{bullet}</span>
                    </div>
                  ))}
                </div>

                {/* CTA inside panel */}
                <div className="mt-4 flex flex-col gap-6 border-t border-slate-100 pt-8 sm:flex-row sm:items-center sm:justify-end">
                  <a
                    href={whatsappUrl('Olá! Gostaria de saber mais sobre o sistema de acompanhamento.')}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={dict.cta}
                  >
                    <Button className="bg-primary text-white hover:bg-primary/90 px-10 py-7 text-xs font-black uppercase tracking-widest shadow-xl shadow-primary/20 transition-all hover:-translate-y-1">
                      {dict.cta}
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom highlights strip */}
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {highlights.map((item) => (
            <div
              key={item.title}
              className="flex items-start gap-4 rounded-xl border border-border bg-card p-4 lg:p-5"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <item.icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground">
                  {item.title}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {item.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
